import React from 'react';
import { LogBox, StatusBar } from 'react-native';
import 'react-native-gesture-handler';
import { ReanimatedLogLevel, configureReanimatedLogger } from 'react-native-reanimated';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { enableFreeze } from 'react-native-screens';

import { AppRoot as AppViewRoot } from '@/components/AppRoot';
import { AuthWebsocketSubscriptions } from '@/components/AuthWebsocketSubscriptions';
import { UserSessionLoader } from '@/feature/authentication/components/UserSessionLoader';
import { GlobalStateProvider } from '@/hooks/use-global-state';
import { NavigationRouter } from '@/navigation/container';
import { AppOrientation } from '@/utils/app-orientation';
import { FloatingModalProvider } from '@/utils/floatingModal/FloatingModalProvider';
import { WebsocketProvider } from '@/utils/websocket/provider';
import { decode } from 'base-64';

import StorybookUIRoot from '../.rnstorybook';
import { HiddenProductRedirectListener } from './components/HiddenProductRedirectListener';
import { RealityReminderWrapper } from './components/RealityReminderWrapper';
import { DataDogAppProvider } from './data/datadog';
import { ModalsProvider } from './feature/alerts/components/ModalsProvider';
import { FavoritesProvider } from './feature/betslip-pickem/components/FavoritesProvider';
import { SinglePlayerProfileProvider } from './feature/fantasy-event-sheets/SinglePlayerProfileProvider';
import { SbkPickbarProvider } from './feature/lobby-sbk/components/SbkPickbarProvider';
import { PopularPlayersDataProvider } from './feature/lobby/hooks/use-popular-players-data';
import { ProductSwitcherChipsPositionsProvider } from './feature/product-transition/components/ProductSwitcherChipsPositionsProvider';
import { MobileBrowserSmartBanner } from './feature/smart-banner/MobileBrowserSmartBanner';
import { useAppSdksInit } from './hooks/use-app-init';
import {
    BetrThemeProvider,
    BonusEngineClientProvider,
    FantasyUrqlClientProvider,
    GestureHandlerRootViewProvider,
    ReactQueryClientProvider,
    TradingCardsClientProvider,
} from './providers';
import { ComposeProviders } from './providers/ComposeProviders';
import { RozeniteDevToolsProvider } from './utils/debug/rozenite';
import { isMobileBrowser } from './utils/is-mobile-browser/is-mobile-browser';

// We disable reanimated strict logs because they are too verbose
// TODO: check existing reanimated animation and ensure they are respecting the new reanimated API (no get/set during render)
configureReanimatedLogger({
    level: ReanimatedLogLevel.warn,
    strict: false, // Disable strict mode
});

let Reactotron = null;

if (__DEV__) {
    Reactotron = require('../configs/reactotron-config').reactotronInstance;
}

//Polyfill for atob https://github.com/auth0/jwt-decode?tab=readme-ov-file#polyfilling-atob
global.atob = decode;

enableFreeze(true);

// due to https://github.com/gorhom/react-native-bottom-sheet/pull/1848
LogBox.ignoreLogs(['[Reanimated] Tried to modify key']);

/**
 * HOC providers and components that will wrap the entire navigation container.
 */
const providers = [
    AppViewRoot,
    SafeAreaProvider,
    GestureHandlerRootViewProvider,
    DataDogAppProvider,
    BetrThemeProvider,
    UserSessionLoader,
    ReactQueryClientProvider,
    WebsocketProvider,
    FantasyUrqlClientProvider,
    BonusEngineClientProvider,
    TradingCardsClientProvider,
    GlobalStateProvider,
    FloatingModalProvider,
];

if (__DEV__) {
    providers.push(RozeniteDevToolsProvider);
}

/**
 * HOC providers and components that will be rendered inside the navigation container.
 * NOTE: These components will be able to access navigation via 'useNavigation()' hook.
 */
const navigationAwareProviders = [
    ModalsProvider,
    FavoritesProvider,
    SinglePlayerProfileProvider,
    SbkPickbarProvider,
    AuthWebsocketSubscriptions,
    RealityReminderWrapper,
    HiddenProductRedirectListener,
    ProductSwitcherChipsPositionsProvider,
    PopularPlayersDataProvider,
];

const App = () => {
    useAppSdksInit();
    AppOrientation.lockToAllowed();

    return (
        <>
            {isMobileBrowser() ? <MobileBrowserSmartBanner /> : null}
            <StatusBar barStyle={'light-content'} translucent backgroundColor={'transparent'} />
            <NavigationRouter providers={navigationAwareProviders} />
        </>
    );
};

const AppWithProviders = () => {
    return (
        <ComposeProviders providers={providers}>
            <App />
        </ComposeProviders>
    );
};

export default Reactotron
    ? Reactotron.storybookSwitcher(() => <StorybookUIRoot />)(AppWithProviders)
    : AppWithProviders;
